import type { ReactNode } from 'react';
import {
    BuildingOffice2Icon,
    Cog6ToothIcon,
    ExclamationTriangleIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import type { PracticeFieldGroup, PracticeFieldGroupId } from './fieldDefinitions';
import { groupFieldsBySubsection } from './fieldDefinitions';
import { DetailFieldGrid, type DetailFieldGridProps } from './DetailFieldGrid';
import { DetailSubsection } from './DetailSubsection';

const GROUP_ICONS: Record<PracticeFieldGroupId, typeof BuildingOffice2Icon> = {
    practice: BuildingOffice2Icon,
    doctor: UserIcon,
    account: Cog6ToothIcon,
    risk: ExclamationTriangleIcon
};

interface DetailGroupCardProps extends Omit<DetailFieldGridProps, 'fields' | 'extraCells'> {
    group: PracticeFieldGroup;
    /** Non-field cells appended to a subsection's grid, keyed by subsection label. */
    subsectionCells?: Partial<Record<string, ReactNode>>;
    /**
     * Header action slot, right-aligned (see D2Group's `action` in the
     * design source) - only the Practice card uses this today, for "Copy
     * address" (see InfoSection's CopyAddressAction). Undefined everywhere
     * else.
     */
    action?: ReactNode;
}

export function DetailGroupCard({
    group,
    action,
    subsectionCells,
    ...gridProps
}: DetailGroupCardProps) {
    const Icon = GROUP_ICONS[group.id];
    const isCompact = gridProps.density === 'compact';
    // Only Account currently defines subsections; Practice/Doctor fields
    // carry no subsectionLabel, so this comes back empty for them and they
    // fall through to the plain flat grid below.
    const subsections = groupFieldsBySubsection(group.fields);

    // shrink-0: inside the Details tab's scroll column (a flex column), cards
    // must keep their natural height - without it flex compresses them to fit
    // and the column never overflows, so it never scrolls.
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden shrink-0">
            <div
                className={`border-b border-gray-100 flex items-center gap-2.5 ${
                    isCompact ? 'px-4 py-1.5' : 'px-5 h-12'
                }`}
            >
                <Icon className="h-4 w-4 text-gray-400" />
                <h3 className="text-[13px] font-bold text-gray-800">{group.label}</h3>
                <span className="text-[12px] text-gray-400">{group.fields.length} fields</span>
                {action && <span className="ml-auto">{action}</span>}
            </div>
            <div className={`flex flex-col ${isCompact ? 'p-3.5 gap-2.5' : 'p-5 gap-5'}`}>
                {subsections.length > 0 ? (
                    subsections.map((subsection, index) => (
                        <DetailSubsection
                            key={subsection.label}
                            label={subsection.label}
                            fields={subsection.fields}
                            divider={index > 0}
                            extraCells={subsectionCells?.[subsection.label]}
                            {...gridProps}
                        />
                    ))
                ) : (
                    <DetailFieldGrid fields={group.fields} {...gridProps} />
                )}
            </div>
        </div>
    );
}
